/**
 * 🚀 UNIFIED BOOKWORKSHEET DATA SLICE
 * 
 * Clean zone-based architecture that eliminates cross-zone contamination.
 * Replaces: bookWorksheetSlice.js, bookWorksheetSlice_upgrade.js, bwVariationVersionSlice.js
 * 
 * Architecture:
 * - characterZone: Pure character operations (no variation data)
 * - variationZone: Pure variation operations (no character data)  
 * - sharedData: Read-only shared data (litGroups, books, selections)
 */

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { apiClient } from '../../apiTools/apiClient';
import { config } from '../../constants/config';

// ===== ASYNC THUNKS (DIRECT API CALLS - NO PASS-THROUGH) =====

// Character Zone Thunks
export const fetchLitGroups = createAsyncThunk(
  'bookWorksheetData/fetchLitGroups',
  async (_, { rejectWithValue }) => {
    try {
      console.log('[BW_DATA] Fetching literary groups');
      const response = await apiClient.get(config.GET_API_DIRECT_LIT_GROUPS);
      return response.data.litGroups;
    } catch (error) {
      console.error('[BW_DATA] Error fetching lit groups:', error);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchLitGroupProfile = createAsyncThunk(
  'bookWorksheetData/fetchLitGroupProfile',
  async (litGroupId, { rejectWithValue }) => {
    try {
      console.log('[BW_DATA] Fetching lit group profile:', litGroupId);
      const response = await apiClient.get(config.GET_API_DIRECT_LIT_GROUP_PROFILE, { litGroupUuid: litGroupId });
      // Extract the profile data from the nested structure
      return { litGroupId, profile: response.data.profile };
    } catch (error) {
      console.error('[BW_DATA] Error fetching lit group profile:', error);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchBooks = createAsyncThunk(
  'bookWorksheetData/fetchBooks',
  async (litGroupId, { rejectWithValue }) => {
    try {
      console.log('[BW_DATA] Fetching books for lit group:', litGroupId);
      const response = await apiClient.get(config.GET_API_DIRECT_BOOKS_BY_LIT_GROUP, { litGroupUuid: litGroupId });
      return response.data.books;
    } catch (error) {
      console.error('[BW_DATA] Error fetching books:', error);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchAllBooks = createAsyncThunk(
  'bookWorksheetData/fetchAllBooks',
  async (_, { rejectWithValue }) => {
    try {
      console.log('[BW_DATA] Fetching all books for user');
      const response = await apiClient.get(config.GET_API_BOOKS_ALL_FOR_USER);

      // The response contains booksByGroup and allBooks
      // We need to ensure each book has its litGroupId property
      const { booksByGroup, allBooks } = response.data;

      // Ensure each book has litGroupId property
      const booksWithGroupIds = allBooks.map(book => {
        // Find which group this book belongs to
        for (const [groupId, books] of Object.entries(booksByGroup)) {
          if (books.some(b => b.uuid === book.uuid)) {
            return { ...book, litGroupId: groupId };
          }
        }
        return book;
      });

      console.log('[BW_DATA] Fetched all books successfully:', booksWithGroupIds.length, 'books');
      return booksWithGroupIds;
    } catch (error) {
      console.error('[BW_DATA] Error fetching all books:', error);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchBookProfile = createAsyncThunk(
  'bookWorksheetData/fetchBookProfile',
  async (bookId, { rejectWithValue }) => {
    try {
      console.log('[BW_DATA] Fetching book profile:', bookId);
      const response = await apiClient.get(config.GET_API_BOOK_PROFILE, { bookUuid: bookId });
      // The API response already contains a profile object, so extract it directly
      const profileData = response.data.profile || response.data;
      return { bookId, profile: profileData };
    } catch (error) {
      console.error('[BW_DATA] Error fetching book profile:', error);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const deleteBook = createAsyncThunk(
  'bookWorksheetData/deleteBook',
  async ({ bookId, options = {} }, { rejectWithValue }) => {
    try {
      console.log('[BW_DATA] Deleting book via unified delete system:', bookId);

      const response = await apiClient.delete(
        `/delete/book/${bookId}`,
        {
          params: {
            cascade: options.cascade || 'cascade',
            cleanup: options.cleanup || 'cleanup',
            deleteType: options.deleteType || 'hard',
            dryRun: options.dryRun || false,
            force: options.force || false,
            ...options
          }
        }
      );

      console.log('[BW_DATA] Unified delete book response:', response.data);
      return {
        bookId,
        ...response.data,
        // Map unified response to expected format
        deletedCount: response.data.deletedCount,
        s3FilesDeleted: response.data.s3FilesDeleted || [],
        cascadeResults: response.data.cascadeResults || {}
      };
    } catch (error) {
      console.error('[BW_DATA] Error deleting book via unified system:', error);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchCharacters = createAsyncThunk(
  'bookWorksheetData/fetchCharacters',
  async (bookId, { rejectWithValue }) => {
    try {
      console.log('[BW_DATA] Fetching characters for book:', bookId);
      const response = await apiClient.get(config.GET_API_DIRECT_CHARACTERS_BY_BOOK, { bookUuid: bookId });
      return response.data.characters;
    } catch (error) {
      console.error('[BW_DATA] Error fetching characters:', error);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchCharactersEnhanced = createAsyncThunk(
  'bookWorksheetData/fetchCharactersEnhanced',
  async (bookId, { rejectWithValue }) => {
    try {
      console.log('[BW_DATA] Fetching enhanced characters for book:', bookId);
      const response = await apiClient.get(config.GET_API_DIRECT_CHARACTERS_ENHANCED, { bookUuid: bookId });

      // Return both the characters and the raw API response for diagnostics
      return {
        characters: response.data.characters,
        rawApiResponse: response.data, // Store the full API response for diagnostics
        bookId
      };
    } catch (error) {
      console.error('[BW_DATA] Error fetching enhanced characters:', error);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchCharacter = createAsyncThunk(
  'bookWorksheetData/fetchCharacter',
  async ({ characterId, bookId }, { rejectWithValue }) => {
    try {
      console.log('[BW_DATA] Fetching character:', characterId);
      const response = await apiClient.get(config.GET_API_DIRECT_CHARACTER_BY_BOOK, {
        bookUuid: bookId,
        characterUuid: characterId
      });
      return response.data;
    } catch (error) {
      console.error('[BW_DATA] Error fetching character:', error);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const createCharacter = createAsyncThunk(
  'bookWorksheetData/createCharacter',
  async ({ bookId, data }, { rejectWithValue }) => {
    try {
      console.log('[BW_DATA] Creating character for book:', bookId);
      const response = await apiClient.post(config.POST_API_ADD_CHAR_TO_BOOK, {
        bookUuid: bookId,
        data: data  // Wrap character data in 'data' field as expected by server
      });
      return response.data;
    } catch (error) {
      console.error('[BW_DATA] Error creating character:', error);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const updateCharacter = createAsyncThunk(
  'bookWorksheetData/updateCharacter',
  async ({ characterId, bookId, data }, { rejectWithValue }) => {
    try {
      console.log('[BW_DATA] Updating character:', characterId);
      const response = await apiClient.put(config.PUT_API_DIRECT_CHARACTER_BY_BOOK, { data: data }, {
        bookUuid: bookId,
        characterUuid: characterId
      });
      return { characterId, ...response.data };
    } catch (error) {
      console.error('[BW_DATA] Error updating character:', error);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const deleteCharacter = createAsyncThunk(
  'bookWorksheetData/deleteCharacter',
  async ({ characterId, bookId, options = {} }, { rejectWithValue }) => {
    try {
      console.log('[BW_DATA] Deleting character via unified delete system:', characterId);

      // Use unified delete endpoint with BookWorksheet entity type
      const response = await apiClient.delete(`${config.DELETE_API_BW_CHARACTER_UNIFIED}/${characterId}`,
        {
          params: {
            bookId,
            cleanup: options.cleanup || 'cleanup',
            cascade: options.cascade || 'cascade',
            dryRun: options.dryRun || false,
            force: options.force || false,
            ...options
          }
        }
      );

      console.log('[BW_DATA] Unified delete response:', response.data);
      return {
        characterId,
        ...response.data,
        // Map unified response to expected format
        deletedCount: response.data.deletedCount,
        s3FilesDeleted: response.data.s3FilesDeleted || [],
        cascadeResults: response.data.cascadeResults || {}
      };
    } catch (error) {
      console.error('[BW_DATA] Error deleting character via unified system:', error);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Thunk to create a new literary group
export const createLitGroup = createAsyncThunk(
  'bookWorksheetData/createLitGroup',
  async (litGroupData, { rejectWithValue }) => {
    try {
      console.log('[BW_DATA] Creating literary group with data wrapper');
      const response = await apiClient.post(config.POST_API_DIRECT_LIT_GROUP, {
        data: litGroupData  // Wrap in data field as expected by server
      });
      return response.data.litGroup || response.data;
    } catch (error) {
      console.error('[BW_DATA] Error creating literary group:', error);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Thunk to update literary group profile  
export const updateLitGroup = createAsyncThunk(
  'bookWorksheetData/updateLitGroup',
  async ({ litGroupUuid, profileData }, { rejectWithValue }) => {
    try {
      console.log('[BW_DATA] Updating literary group profile with data wrapper:', litGroupUuid);
      const response = await apiClient.put(config.PUT_API_DIRECT_LIT_GROUP_PROFILE,
        { data: profileData },  // Wrap in data field
        { litGroupUuid }
      );
      return response.data.profile || response.data;
    } catch (error) {
      console.error('[BW_DATA] Error updating literary group:', error);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Thunk to create a new book
export const createBook = createAsyncThunk(
  'bookWorksheetData/createBook',
  async (bookData, { rejectWithValue }) => {
    try {
      console.log('[BW_DATA] Creating book with data wrapper:', bookData);

      // Handle the nested structure from BookForm-enh
      // bookData comes in as: { litGroupId, data: { ...bookFields }, bookId }
      let payload;

      if (bookData.data) {
        // If data is nested, extract and flatten it
        const { litGroupId, data, bookId } = bookData;
        payload = {
          data: {
            litGroupId,
            userId: data.userId,
            name: data.name || data.title,  // Use name or fallback to title
            title: data.title || data.name,
            description: data.description,
            authorFirstName: data.authorFirstName,
            authorLastName: data.authorLastName,
            series: data.series || '',
            priority: data.priority || 'Medium',
            status: data.status || 'Active',
            deadline: data.deadline,
            bookCover: data.bookCover || '',
            uuid: bookId || undefined  // Use provided bookId as uuid
          }
        };
      } else {
        // Handle flat structure for backward compatibility
        const { litGroupId, ...bookDetails } = bookData;
        payload = {
          data: {
            litGroupId,
            ...bookDetails
          }
        };
      }

      console.log('[BW_DATA] Sending book creation payload:', payload);
      const response = await apiClient.post(config.ADD_API_LIT_GROUP_BOOK, payload);
      return response.data.book || response.data;
    } catch (error) {
      console.error('[BW_DATA] Error creating book:', error);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Thunk to update book profile
export const updateBook = createAsyncThunk(
  'bookWorksheetData/updateBook',
  async ({ bookUuid, profileData }, { rejectWithValue }) => {
    try {
      console.log('[BW_DATA] Updating book profile with data wrapper:', bookUuid);
      const response = await apiClient.put(config.PUT_API_BOOK_PROFILE,
        { data: profileData },  // Wrap in data field
        { bookUuid }
      );
      return response.data.profile || response.data;
    } catch (error) {
      console.error('[BW_DATA] Error updating book:', error);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Variation Zone Thunks
export const fetchVariations = createAsyncThunk(
  'bookWorksheetData/fetchVariations',
  async ({ litGroupId, bookId, characterId, forceRefresh }, { rejectWithValue }) => {
    try {
      console.log('[BW_DATA] Fetching variations for character:', characterId, {
        litGroupId,
        bookId,
        forceRefresh
      });

      // Build API parameters - include optional context if provided
      const apiParams = { characterUuid: characterId };
      if (litGroupId) apiParams.litGroupUuid = litGroupId;
      if (bookId) apiParams.bookUuid = bookId;

      // const response = await apiClient.get(config.GET_API_DIRECT_VARIATIONS_BY_CHARACTER, apiParams);
      // ✅ CORRECT: Pass apiParams as the 3rd parameter (queryParams)
      const response = await apiClient.get(
        config.GET_API_DIRECT_VARIATIONS_BY_CHARACTER,
        { characterUuid: characterId },  // 2nd param: URL substitution (for :characterUuid in URL)
        apiParams                        // 3rd param: Query parameters (becomes req.query)
      );

      // Add characterId to each variation for filtering
      const variations = response.data.variations.map(variation => ({
        ...variation,
        characterId
      }));

      return { variations, characterId, litGroupId, bookId };
    } catch (error) {
      console.error('[BW_DATA] Error fetching variations:', error);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchVariationCounts = createAsyncThunk(
  'bookWorksheetData/fetchVariationCounts',
  async ({ litGroupId, bookId, characterId, forceRefresh }, { rejectWithValue }) => {
    try {
      console.log('[BW_DATA] Fetching variation counts for character:', characterId, {
        litGroupId,
        bookId,
        forceRefresh
      });

      // Build query parameters - include optional context if provided
      const queryParams = { charUuid: characterId };
      if (litGroupId) queryParams.litGroupUuid = litGroupId;
      if (bookId) queryParams.bookUuid = bookId;

      const response = await apiClient.get(config.GET_VARIATION_COUNTS, {}, queryParams);

      // The response should contain variation counts keyed by variation UUID
      // Try multiple possible response structures to match the old slice
      const counts = response.data.counts || response.data.variationCounts || response.data || {};
      console.log('[BW_DATA] Received variation counts:', counts);
      console.log('[BW_DATA] Full API response:', response.data);

      return { characterId, litGroupId, bookId, counts };
    } catch (error) {
      console.error('[BW_DATA] Error fetching variation counts:', error);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const loadVariationForEdit = createAsyncThunk(
  'bookWorksheetData/loadVariationForEdit',
  async ({ variationId, characterId, litGroupId, bookId }, { getState, rejectWithValue }) => {
    try {
      console.log('[BW_DATA] Loading variation for edit:', variationId);

      // First, try to get the variation from the current state
      const state = getState();
      const currentVariations = state.user.bookWorksheetData?.variationZone?.variations || [];
      const currentVariation = currentVariations.find(v => v.uuid === variationId);

      // if (!currentVariation) {
      //   console.error('[BW_DATA] Variation not found in current state:', variationId);
      //   throw new Error('Variation not found in current state');
      // }

      // Fetch variation data with samples using the specific variation endpoint
      const response = await apiClient.get(config.GET_CHAR_VAR_SAMPLES, {}, {
        litGroupUuid: litGroupId,
        bookUuid: bookId,
        charUuid: characterId
      });

      console.log('[BW_DATA] GET_CHAR_VAR_SAMPLES API response:', {
        fullResponse: response.data,
        characterList: response.data.characterList,
        characterListLength: response.data.characterList?.length,
        firstCharacter: response.data.characterList?.[0]
      });

      const characterData = response.data.characterList?.[0];
      if (!characterData) {
        console.error('[BW_DATA] Character data not found in API response');
        throw new Error('Character data not found');
      }

      console.log('[BW_DATA] Character data found:', {
        characterUuid: characterData.uuid,
        characterData: characterData, // Show full character structure
        variationsCount: characterData.variations?.length,
        variations: characterData.variations?.map(v => ({ uuid: v.uuid, name: v.name, fullVariation: v })),
        lookingForVariationId: variationId
      });

      // Find the variation in the response - the variation data is nested inside a 'variation' field
      let variationItem = characterData.variations?.find(v => v.variation?.uuid === variationId);

      console.log('[BW_DATA] Variation search result:', {
        found: !!variationItem,
        variationItem: variationItem,
        searchedFor: variationId,
        availableVariations: characterData.variations?.map(v => ({
          keys: Object.keys(v),
          variationKeys: v.variation ? Object.keys(v.variation) : [],
          variationUuid: v.variation?.uuid,
          variationName: v.variation?.name,
          speechSamplesCount: v.speechSamples?.length || 0,
          fullObject: v
        }))
      });

      // Extract the actual variation object from the nested structure
      const variation = variationItem?.variation;
      if (!variation) {
        // If not found in API response, use the current variation data and create empty samples
        console.warn('[BW_DATA] Variation not found in API response, using current state data');
        return {
          variation: {
            uuid: currentVariation.uuid,
            name: currentVariation.name,
            content: currentVariation.content || '',
            activeSampleUuid: currentVariation.activeSampleUuid || null,
            priority: currentVariation.priority || 'medium',
            status: currentVariation.status || 'not-started',
            deadline: currentVariation.deadline || null,
            version: currentVariation.version || 1
          },
          samples: [], // Empty samples if not found in API
          characterId,
          litGroupId,
          bookId
        };
      }

      // Process speech samples from the nested structure
      const samples = variationItem.speechSamples?.map(sample => {
        // Handle the case where speechSample might be null
        if (!sample.speechSample) {
          return null;
        }

        const speechSample = sample.speechSample;
        const textSnippet = sample.textSnippets && sample.textSnippets.length > 0
          ? sample.textSnippets[0]
          : null;
        const voiceSnippet = sample.voiceSnippets && sample.voiceSnippets.length > 0
          ? sample.voiceSnippets[0]
          : null;

        return {
          uuid: speechSample.uuid,
          version: speechSample.revision || speechSample.version || 1,
          s3Url: voiceSnippet ? voiceSnippet.s3Url : '',
          textSnippet: textSnippet ? textSnippet.content || textSnippet.textSnippet || 'No text available' : 'No text available',
          createdAt: speechSample.createdAt,
          isActive: speechSample.uuid === variation.activeSampleUuid,
          fileDuration: voiceSnippet ? voiceSnippet.fileDuration : 0
        };
      }).filter(sample => sample !== null) || [];

      // Extract content from the first sample's textSnippet if variation.content is empty
      const variationContent = variation.content ||
        (samples.length > 0 ? samples[0].textSnippet : '') ||
        currentVariation.content || '';

      console.log('[BW_DATA] Content extraction:', {
        variationContent: variation.content,
        firstSampleText: samples.length > 0 ? samples[0].textSnippet : 'no samples',
        currentVariationContent: currentVariation.content,
        finalContent: variationContent
      });

      return {
        variation: {
          uuid: variation.uuid,
          name: variation.name,
          content: variationContent, // Use extracted content
          activeSampleUuid: variation.activeSampleUuid,
          priority: variation.priority || currentVariation.priority || 'medium',
          status: variation.status || currentVariation.status || 'not-started',
          deadline: variation.deadline || currentVariation.deadline || null,
          version: variation.version || currentVariation.version || 1
        },
        samples,
        characterId,
        litGroupId,
        bookId
      };
    } catch (error) {
      console.error('[BW_DATA] Error loading variation for edit:', error);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const createVariation = createAsyncThunk(
  'bookWorksheetData/createVariation',
  async ({ characterId, data }, { rejectWithValue }) => {
    try {
      console.log('[BW_DATA] Creating variation for character:', characterId);
      const response = await apiClient.post(config.POST_API_DIRECT_VARIATION, { data: data }, {
        characterUuid: characterId
      });
      return response.data;
    } catch (error) {
      console.error('[BW_DATA] Error creating variation:', error);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const updateVariation = createAsyncThunk(
  'bookWorksheetData/updateVariation',
  async ({ variationId, data }, { rejectWithValue }) => {
    try {
      console.log('[BW_DATA] Updating variation:', variationId);
      const response = await apiClient.put(config.PUT_API_DIRECT_VARIATION, { data: data }, {
        id: variationId
      });

      // 🔍 WRAPPER FIX LOGGING - Show what we're extracting
      console.log('[BW_DATA] updateVariation - API RESPONSE ANALYSIS:');
      console.log('- Raw response.data:', response.data);
      console.log('- response.data keys:', response.data ? Object.keys(response.data) : 'null');
      console.log('- response.data.variation:', response.data?.variation);
      console.log('- response.data.variation keys:', response.data?.variation ? Object.keys(response.data.variation) : 'null');

      // ✅ PROPER FIX: Extract variation from nested response structure
      const extractedVariation = response.data?.variation || response.data;
      console.log('- Extracted variation:', extractedVariation);
      console.log('- Extracted variation.activeSampleUuid:', extractedVariation?.activeSampleUuid);

      return {
        variationId,
        variation: extractedVariation,  // ✅ Extract the actual variation object
        isCreate: false
      };
    } catch (error) {
      console.error('[BW_DATA] Error updating variation:', error);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const deleteVariation = createAsyncThunk(
  'bookWorksheetData/deleteVariation',
  async ({ variationId, options = {} }, { rejectWithValue }) => {
    try {
      console.log('[BW_DATA] Deleting variation via unified delete system:', variationId);

      // Use unified delete endpoint with BookWorksheet entity type
      const response = await apiClient.delete(
        `/delete/bw-variation/${variationId}`,
        {
          params: {
            cleanup: options.cleanup || 'cleanup',
            cascade: options.cascade || 'cascade',
            dryRun: options.dryRun || false,
            force: options.force || false,
            ...options
          }
        }
      );

      console.log('[BW_DATA] Unified delete variation response:', response.data);
      return {
        variationId,
        ...response.data,
        // Map unified response to expected format
        deletedCount: response.data.deletedCount,
        s3FilesDeleted: response.data.s3FilesDeleted || [],
        cascadeResults: response.data.cascadeResults || {}
      };
    } catch (error) {
      console.error('[BW_DATA] Error deleting variation via unified system:', error);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const deleteVersion = createAsyncThunk(
  'bookWorksheetData/deleteVersion',
  async ({ versionId, variationId, options = {} }, { rejectWithValue }) => {
    try {
      console.log('[BW_DATA] Deleting speech sample via unified delete system:', versionId);

      // Use unified delete endpoint with BookWorksheet entity type
      const response = await apiClient.delete(
        `/delete/bw-speech-sample/${versionId}`,
        {
          params: {
            cleanup: options.cleanup || 'cleanup',
            dryRun: options.dryRun || false,
            force: options.force || false,
            ...options
          }
        }
      );

      console.log('[BW_DATA] Unified delete speech sample response:', response.data);
      return {
        versionId,
        variationId,
        ...response.data,
        // Map unified response to expected format
        deletedCount: response.data.deletedCount,
        s3FilesDeleted: response.data.s3FilesDeleted || [],
        cascadeResults: response.data.cascadeResults || {}
      };
    } catch (error) {
      console.error('[BW_DATA] Error deleting speech sample via unified system:', error);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);


export const loadVersionForEdit = createAsyncThunk(
  'bookWorksheetData/loadVersionForEdit',
  async ({ variationId, versionId, characterId, litGroupId, bookId }, { rejectWithValue }) => {
    try {
      console.log('[BW_DATA] Loading specific version for edit:', versionId, 'from variation:', variationId);

      // For now, this is similar to loadVariationForEdit but focuses on a specific version
      // TODO: Implement specific version loading if API supports it
      const response = await apiClient.get(config.GET_CHAR_VAR_SAMPLES, {}, {
        litGroupUuid: litGroupId,
        bookUuid: bookId,
        charUuid: characterId
      });

      const characterData = response.data.characterList?.[0];
      if (!characterData) {
        throw new Error('Character data not found');
      }

      const variationItem = characterData.variations?.find(v => v.variation?.uuid === variationId);
      if (!variationItem) {
        throw new Error('Variation not found');
      }

      const variation = variationItem.variation;
      const samples = variationItem.speechSamples?.map(sample => {
        if (!sample.speechSample) return null;

        const speechSample = sample.speechSample;
        const textSnippet = sample.textSnippets?.[0];
        const voiceSnippet = sample.voiceSnippets?.[0];

        return {
          uuid: speechSample.uuid,
          version: speechSample.revision || speechSample.version || 1,
          s3Url: voiceSnippet?.s3Url || '',
          textSnippet: textSnippet?.content || textSnippet?.textSnippet || 'No text available',
          createdAt: speechSample.createdAt,
          isActive: speechSample.uuid === variation.activeSampleUuid,
          fileDuration: voiceSnippet?.fileDuration || 0
        };
      }).filter(sample => sample !== null) || [];

      // Find the specific version/sample
      const targetSample = samples.find(s => s.uuid === versionId);
      if (!targetSample) {
        throw new Error('Version not found');
      }

      return {
        variation,
        samples,
        targetVersion: targetSample,
        characterId,
        litGroupId,
        bookId
      };
    } catch (error) {
      console.error('[BW_DATA] Error loading version for edit:', error);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const loadDefaultVersion = createAsyncThunk(
  'bookWorksheetData/loadDefaultVersion',
  async ({ characterId }, { getState, rejectWithValue }) => {
    try {
      console.log('[BW_DATA] Loading default version for character:', characterId);

      const state = getState();
      const sharedData = state.user.bookWorksheetData?.sharedData;
      const litGroupId = sharedData?.selections?.litGroupId;
      const bookId = sharedData?.selections?.bookId;

      if (!litGroupId || !bookId) {
        throw new Error('Missing litGroupId or bookId for default version');
      }

      // Create default variation structure
      const defaultVariation = {
        uuid: null, // Will be set when created
        name: '',
        content: '',
        activeSampleUuid: null,
        priority: 'medium',
        status: 'not-started',
        deadline: null,
        version: 1
      };

      // Create default active version
      const defaultActiveVersion = {
        uuid: null,
        textSnippet: '',
        s3Url: '',
        fileDuration: 0,
        version: 1,
        isActive: false
      };

      return {
        variation: defaultVariation,
        activeVersion: defaultActiveVersion,
        samples: [],
        characterId,
        litGroupId,
        bookId
      };
    } catch (error) {
      console.error('[BW_DATA] Error loading default version:', error);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const loadVersionForClone = createAsyncThunk(
  'bookWorksheetData/loadVersionForClone',
  async ({ variationId, versionId, characterId, litGroupId, bookId }, { rejectWithValue }) => {
    try {
      console.log('[BW_DATA] Loading version for cloning:', versionId, 'from variation:', variationId);

      // First load the variation and samples
      const response = await apiClient.get(config.GET_CHAR_VAR_SAMPLES, {}, {
        litGroupUuid: litGroupId,
        bookUuid: bookId,
        charUuid: characterId
      });

      const characterData = response.data.characterList?.[0];
      if (!characterData) {
        throw new Error('Character data not found');
      }

      const variationItem = characterData.variations?.find(v => v.variation?.uuid === variationId);
      if (!variationItem) {
        throw new Error('Variation not found');
      }

      const sourceVariation = variationItem.variation;
      const samples = variationItem.speechSamples?.map(sample => {
        if (!sample.speechSample) return null;

        const speechSample = sample.speechSample;
        const textSnippet = sample.textSnippets?.[0];
        const voiceSnippet = sample.voiceSnippets?.[0];

        return {
          uuid: speechSample.uuid,
          version: speechSample.revision || speechSample.version || 1,
          s3Url: voiceSnippet?.s3Url || '',
          textSnippet: textSnippet?.content || textSnippet?.textSnippet || 'No text available',
          createdAt: speechSample.createdAt,
          isActive: speechSample.uuid === sourceVariation.activeSampleUuid,
          fileDuration: voiceSnippet?.fileDuration || 0
        };
      }).filter(sample => sample !== null) || [];

      // Find the specific version to clone
      const sourceVersion = samples.find(s => s.uuid === versionId);
      if (!sourceVersion) {
        throw new Error('Source version not found');
      }

      // Create new variation based on source
      const clonedVariation = {
        uuid: null, // Will be set when created
        name: `${sourceVariation.name} (Copy)`,
        content: sourceVersion.textSnippet,
        activeSampleUuid: null,
        priority: sourceVariation.priority || 'medium',
        status: 'not-started',
        deadline: null,
        version: 1
      };

      // Create active version based on source version
      const clonedActiveVersion = {
        uuid: null,
        textSnippet: sourceVersion.textSnippet,
        s3Url: '', // Don't copy audio
        fileDuration: 0,
        version: 1,
        isActive: false
      };

      return {
        variation: clonedVariation,
        activeVersion: clonedActiveVersion,
        samples: [], // Start with no samples for new variation
        sourceVariation,
        sourceVersion,
        characterId,
        litGroupId,
        bookId
      };
    } catch (error) {
      console.error('[BW_DATA] Error loading version for clone:', error);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const saveVariation = createAsyncThunk(
  'bookWorksheetData/saveVariation',
  async ({ variationData, versionData, characterId }, { rejectWithValue }) => {
    try {
      // Determine if this is a create operation based on whether variation has a uuid
      const isCreate = !variationData.uuid;
      console.log('[BW_DATA] Saving variation, isCreate:', isCreate);

      if (isCreate) {
        // Create new variation
        const createData = {
          name: variationData.name,
          content: versionData.textSnippet,
          priority: variationData.priority || 'medium',
          status: variationData.status || 'not-started',
          deadline: variationData.deadline,
          version: variationData.version || 1
        };

        const response = await apiClient.post(config.POST_API_DIRECT_VARIATION, createData, {
          characterUuid: characterId
        });

        return {
          variation: response.data,
          isCreate: true
        };
      } else {
        // Update existing variation
        const updateData = {
          name: variationData.name,
          content: versionData.textSnippet,
          priority: variationData.priority,
          status: variationData.status,
          deadline: variationData.deadline,
          version: variationData.version
        };

        const response = await apiClient.put(config.PUT_API_DIRECT_VARIATION, updateData, {
          id: variationData.uuid
        });

        // 🔍 WRAPPER FIX LOGGING - Show what we're extracting in saveVariation
        console.log('[BW_DATA] saveVariation - API RESPONSE ANALYSIS:');
        console.log('- Raw response.data:', response.data);
        console.log('- response.data.variation:', response.data?.variation);

        // ✅ PROPER FIX: Extract variation from nested response structure
        const extractedVariation = response.data?.variation || response.data;
        console.log('- Extracted variation:', extractedVariation);
        console.log('- Extracted variation.activeSampleUuid:', extractedVariation?.activeSampleUuid);

        return {
          variation: extractedVariation,  // ✅ Extract the actual variation object
          variationId: variationData.uuid,
          isCreate: false
        };
      }
    } catch (error) {
      console.error('[BW_DATA] Error saving variation:', error);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const updateActiveSample = createAsyncThunk(
  'bookWorksheetData/updateActiveSample',
  async ({ variationId, sampleId, characterId }, { rejectWithValue }) => {
    try {
      console.log('[BW_DATA] Updating active sample:', sampleId, 'for variation:', variationId);

      // Update the variation to set the new active sample
      const response = await apiClient.put(config.PUT_API_DIRECT_VARIATION, {
        activeSampleUuid: sampleId
      }, {
        id: variationId
      });

      return {
        variationId,
        sampleId,
        variation: response.data
      };
    } catch (error) {
      console.error('[BW_DATA] Error updating active sample:', error);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

/**
 * Update TextSnippet node speech sample record in database
 */
export const updateTextSnippetDb = createAsyncThunk(
  'bookWorksheetData/updateTextSnippetDb',
  async ({ bookuuid, sampleuuid, s3Url, content }, { rejectWithValue }) => {
    try {
      console.log('[BW_DATA] Updating text snippet:', { bookuuid, sampleuuid, hasS3Url: !!s3Url, hasContent: content !== undefined });

      // Input validation
      if (!bookuuid || !sampleuuid) {
        return rejectWithValue('bookuuid and sampleuuid are required parameters');
      }

      // Validate at least one updateable property is provided
      if (!s3Url && content === undefined) {
        return rejectWithValue('At least one property (s3Url or content) must be provided for update');
      }

      // Build query parameters dynamically
      const queryParams = { bookuuid, sampleuuid };
      const updatedFields = [];

      if (s3Url) {
        queryParams.s3Url = s3Url;
        updatedFields.push('s3TextUrl');
      }

      if (content !== undefined) {
        queryParams.content = content;
        updatedFields.push('content');
      }
      console.log("config.UPDATE_TEXT_SNIPPET", config.UPDATE_TEXT_SNIPPET);
      // Make API call
      const response = await apiClient.put(
        config.UPDATE_TEXT_SNIPPET,
        {}, // Empty body
        {}, // No URL params to substitute
        { params: queryParams } // Query parameters in config
      );
      console.log('[BW_DATA] Text snippet updated successfully:', updatedFields);

      return {
        bookuuid,
        sampleuuid,
        updatedFields,
        charSpeechSample: response.data.charSpeechSample,
        textSnippet: response.data.textSnippet,
        s3Url: s3Url || null,
        content: content || null,
        timestamp: Date.now()
      };
    } catch (error) {
      console.error('[BW_DATA] Error updating text snippet:', error);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);




// ===== INITIAL STATE =====
const initialState = {
  // Character Zone (Pure - no variation data)
  characterZone: {
    characters: [],
    selectedCharacter: null,
    loading: false,
    error: null,
    lastUpdated: null
  },

  // Variation Zone (Pure - no character data except ID)
  variationZone: {
    variations: [],
    activeVariation: null,
    activeVersion: null,
    samples: [],
    variationCounts: {},
    loading: false,
    error: null,
    lastUpdated: null,

    // Edit state
    editMode: 'view', // 'view' | 'edit' | 'create'
    hasUnsavedChanges: false,
    isSubmitting: false
  },

  // Shared Data (Read-only shared state)
  sharedData: {
    litGroups: [],
    books: [],
    litGroupProfiles: {},
    bookProfiles: {},

    // Current selections
    selections: {
      litGroupId: null,
      bookId: null,
      characterId: null,
      variationId: null
    },

    // Loading states
    loading: {
      litGroups: false,
      books: false,
      profiles: false
    },

    // Errors
    errors: {
      litGroups: null,
      books: null,
      profiles: null
    },

    lastUpdated: {
      litGroups: null,
      books: null,
      profiles: null
    }
  }
};

// ===== SLICE DEFINITION =====
const bookWorksheetDataSlice = createSlice({
  name: 'bookWorksheetData',
  initialState,
  reducers: {
    // ===== SELECTION ACTIONS =====
    setLitGroupSelection: (state, action) => {
      const litGroupId = action.payload;
      state.sharedData.selections.litGroupId = litGroupId;

      // Clear dependent selections
      state.sharedData.selections.bookId = null;
      state.sharedData.selections.characterId = null;
      state.sharedData.selections.variationId = null;

      console.log('[BW_DATA] Set lit group selection:', litGroupId);
    },

    // Auto-select first book when books are fetched
    autoSelectFirstBook: (state) => {
      const books = state.sharedData.books;
      console.log('[BW_DATA] Auto-selected book: books', books);
      if (books && books.length > 0 && !state.sharedData.selections.bookId) {
        // Find a book with a profile (not profileMissing) or select the first one
        const bookWithProfile = books.find(book => !book.profileMissing);
        const selectedBook = bookWithProfile || books[0];

        state.sharedData.selections.bookId = selectedBook.uuid;
        console.log('[BW_DATA] Auto-selected book:', selectedBook.uuid, selectedBook.name);
      }
    },
    autoSelectFirstCharacter: (state) => {
      const characters = state.sharedData.characters;
      if (characters && characters.length > 0 && !state.sharedData.selections.bookId) {
        // Find a selectedCharacter with a profile (not profileMissing) or select the first one
        const characterWithProfile = characters.find(character => !character.profileMissing);
        const selectedCharacter = characterWithProfile || characters[0];

        state.sharedData.selections.characterId = selectedCharacter.uuid;
        console.log('[BW_DATA] Auto-selected selectedCharacter:', selectedCharacter.uuid, selectedCharacter.name);
      }
    },
    setBookSelection: (state, action) => {
      const bookId = action.payload;
      state.sharedData.selections.bookId = bookId;

      // Clear dependent selections
      state.sharedData.selections.characterId = null;
      state.sharedData.selections.variationId = null;

      console.log('[BW_DATA] Set book selection:', bookId);
    },

    setCharacterSelection: (state, action) => {
      const characterId = action.payload;

      console.log('🎯 [BW_DATA_SLICE] setCharacterSelection called with:', characterId);
      console.log('🎯 [BW_DATA_SLICE] Current character selection:', state.sharedData.selections.characterId);

      // Prevent duplicate selections
      if (state.sharedData.selections.characterId === characterId) {
        console.log('🎯 [BW_DATA_SLICE] Skipping duplicate character selection:', characterId);
        return; // No state change = no re-renders
      }

      console.log('🎯 [BW_DATA_SLICE] Updating character selection from', state.sharedData.selections.characterId, 'to', characterId);
      state.sharedData.selections.characterId = characterId;

      // Clear dependent selections
      state.sharedData.selections.variationId = null;

      // Update selected character in character zone
      state.characterZone.selectedCharacter = state.characterZone.characters.find(
        char => char.uuid === characterId
      ) || null;

      console.log('🎯 [BW_DATA_SLICE] Character selection updated successfully:', characterId);
    },

    setVariationSelection: (state, action) => {
      const variationId = action.payload;
      state.sharedData.selections.variationId = variationId;

      // Update active variation in variation zone
      state.variationZone.activeVariation = state.variationZone.variations.find(
        variation => variation.uuid === variationId
      ) || null;

      console.log('[BW_DATA] Set variation selection:', variationId);
    },

    // ===== VARIATION ZONE ACTIONS =====
    updateVersionField: (state, action) => {
      const { field, value } = action.payload;
      if (state.variationZone.activeVersion) {
        state.variationZone.activeVersion[field] = value;
        state.variationZone.hasUnsavedChanges = true;

        // console.log('[BW_DATA] Updated version field:', field, '=', value);
      }
    },

    setActiveVersionFromSamples: (state, action) => {
      const sampleUuid = action.payload;
      const sample = state.variationZone.samples.find(s => s.uuid === sampleUuid);

      if (sample) {
        state.variationZone.activeVersion = {
          uuid: sample.uuid,
          textSnippet: sample.textSnippet,
          s3Url: sample.s3Url,
          fileDuration: sample.fileDuration,
          version: sample.version,
          isActive: true
        };

        // Update active sample UUID in variation
        if (state.variationZone.activeVariation) {
          state.variationZone.activeVariation.activeSampleUuid = sampleUuid;
        }

        console.log('[BW_DATA] Set active version from samples:', sampleUuid);
      }
    },

    setEditMode: (state, action) => {
      state.variationZone.editMode = action.payload;
      console.log('[BW_DATA] Set edit mode:', action.payload);
    },

    clearUnsavedChanges: (state) => {
      state.variationZone.hasUnsavedChanges = false;
    },

    setSubmitting: (state, action) => {
      state.variationZone.isSubmitting = action.payload;
    },

    // ===== CLEAR ACTIONS =====
    clearCharacterZone: (state) => {
      state.characterZone.characters = [];
      state.characterZone.selectedCharacter = null;
      state.characterZone.error = null;
    },

    clearVariationZone: (state) => {
      state.variationZone.variations = [];
      state.variationZone.activeVariation = null;
      state.variationZone.activeVersion = null;
      state.variationZone.samples = [];
      state.variationZone.editMode = 'view';
      state.variationZone.hasUnsavedChanges = false;
      state.variationZone.error = null;
    },

    // ===== MISSING SYNCHRONOUS ACTIONS =====
    updateFormField: (state, action) => {
      const { field, value } = action.payload;
      // For now, just update the active variation field
      if (state.variationZone.activeVariation) {
        state.variationZone.activeVariation[field] = value;
        state.variationZone.hasUnsavedChanges = true;
      }
    },

    setFormDirty: (state, action) => {
      state.variationZone.hasUnsavedChanges = action.payload;
    },

    setFormSubmitting: (state, action) => {
      state.variationZone.isSubmitting = action.payload;
    },

    setEditError: (state, action) => {
      const { error } = action.payload;
      state.variationZone.error = error;
    },

    clearEditError: (state) => {
      state.variationZone.error = null;
    },

    clearEditForm: (state) => {
      state.variationZone.activeVariation = null;
      state.variationZone.activeVersion = null;
      state.variationZone.samples = [];
      state.variationZone.editMode = 'view';
      state.variationZone.hasUnsavedChanges = false;
      state.variationZone.isSubmitting = false;
      state.variationZone.error = null;
    },

    setRepositoryVersion: (state, action) => {
      // Store the repository version for cancel operations
      // For now, we'll store it in a new field
      state.variationZone.repositoryVersion = action.payload;
    },

    showErrorHint: (state, action) => {
      // Store error hint message
      state.variationZone.errorHint = action.payload;
    }
  },

  extraReducers: (builder) => {
    builder
      // ===== LIT GROUPS =====
      .addCase(fetchLitGroups.pending, (state) => {
        state.sharedData.loading.litGroups = true;
        state.sharedData.errors.litGroups = null;
      })
      .addCase(fetchLitGroups.fulfilled, (state, action) => {
        state.sharedData.loading.litGroups = false;
        state.sharedData.litGroups = action.payload;
        state.sharedData.lastUpdated.litGroups = Date.now();
        console.log('[BW_DATA] Stored lit groups in new slice:', action.payload.length, 'groups');
        console.log('[BW_DATA] First lit group:', action.payload[0]);
      })
      .addCase(fetchLitGroups.rejected, (state, action) => {
        state.sharedData.loading.litGroups = false;
        state.sharedData.errors.litGroups = action.payload;
      })

      // ===== LIT GROUP PROFILES =====
      .addCase(fetchLitGroupProfile.pending, (state) => {
        state.sharedData.loading.profiles = true;
        state.sharedData.errors.profiles = null;
      })
      .addCase(fetchLitGroupProfile.fulfilled, (state, action) => {
        const { litGroupId, profile } = action.payload;
        state.sharedData.loading.profiles = false;
        state.sharedData.litGroupProfiles[litGroupId] = profile;
        state.sharedData.lastUpdated.profiles = Date.now();
        console.log('[BW_DATA] Stored lit group profile for:', litGroupId);
        console.log('[BW_DATA] Profile data:', profile);
      })
      .addCase(fetchLitGroupProfile.rejected, (state, action) => {
        state.sharedData.loading.profiles = false;
        state.sharedData.errors.profiles = action.payload;
        console.error('[BW_DATA] Failed to fetch lit group profile:', action.payload);
      })

      // ===== BOOKS =====
      .addCase(fetchBooks.pending, (state) => {
        state.sharedData.loading.books = true;
        state.sharedData.errors.books = null;
      })
      .addCase(fetchBooks.fulfilled, (state, action) => {
        state.sharedData.loading.books = false;

        // Get the new books from the API response
        const newBooks = action.payload || [];

        // If there are new books, merge them with existing books
        if (newBooks.length > 0) {
          const existingBooks = state.sharedData.books || [];

          // Get the litGroupId from the first book (they all have the same litGroupId)
          const litGroupId = newBooks[0]?.litGroupId;

          if (litGroupId) {
            // Remove any existing books for this literary group
            const booksFromOtherGroups = existingBooks.filter(
              book => book.litGroupId !== litGroupId && book.literaryGroupId !== litGroupId
            );

            // Add the new books for this literary group
            state.sharedData.books = [...booksFromOtherGroups, ...newBooks];
          } else {
            // Fallback: if no litGroupId, just replace (shouldn't happen with our API fix)
            state.sharedData.books = newBooks;
          }
        }
        // If no new books, keep existing books (don't clear them)

        state.sharedData.lastUpdated.books = Date.now();

        console.log('[BW_DATA] Updated books array:', {
          newBooksCount: newBooks.length,
          totalBooksCount: state.sharedData.books.length,
          litGroupId: newBooks[0]?.litGroupId
        });
      })
      .addCase(fetchBooks.rejected, (state, action) => {
        state.sharedData.loading.books = false;
        state.sharedData.errors.books = action.payload;
      })

      // ===== FETCH ALL BOOKS =====
      .addCase(fetchAllBooks.pending, (state) => {
        state.sharedData.loading.books = true;
        state.sharedData.errors.books = null;
      })
      .addCase(fetchAllBooks.fulfilled, (state, action) => {
        state.sharedData.loading.books = false;

        // Replace all books with the fetched books
        state.sharedData.books = action.payload || [];
        state.sharedData.lastUpdated.books = Date.now();

        console.log('[BW_DATA] Stored all books:', state.sharedData.books.length, 'books');
      })
      .addCase(fetchAllBooks.rejected, (state, action) => {
        state.sharedData.loading.books = false;
        state.sharedData.errors.books = action.payload;
      })

      // ===== BOOK PROFILES =====
      .addCase(fetchBookProfile.pending, (state) => {
        state.sharedData.loading.profiles = true;
        state.sharedData.errors.profiles = null;
      })
      .addCase(fetchBookProfile.fulfilled, (state, action) => {
        const { bookId, profile } = action.payload;
        state.sharedData.loading.profiles = false;
        state.sharedData.bookProfiles[bookId] = profile;
        state.sharedData.lastUpdated.profiles = Date.now();
        console.log('[BW_DATA] Stored book profile for:', bookId);
        console.log('[BW_DATA] Book profile data:', profile);
      })
      .addCase(fetchBookProfile.rejected, (state, action) => {
        state.sharedData.loading.profiles = false;
        state.sharedData.errors.profiles = action.payload;
        console.error('[BW_DATA] Failed to fetch book profile:', action.payload);
      })

      // ===== DELETE BOOK =====
      .addCase(deleteBook.fulfilled, (state, action) => {
        const { bookId } = action.payload;

        // Remove from books array
        if (state.sharedData.books) {
          state.sharedData.books = state.sharedData.books.filter(book => book.uuid !== bookId);
        }

        // Remove book profile if it exists
        if (state.sharedData.bookProfiles[bookId]) {
          delete state.sharedData.bookProfiles[bookId];
        }

        // Clear selection if deleted book was selected
        if (state.sharedData.selections.bookId === bookId) {
          state.sharedData.selections.bookId = null;
          state.sharedData.selections.characterId = null;
          state.sharedData.selections.variationId = null;
        }

        // Clear character zone if it was for this book
        state.characterZone.characters = [];
        state.characterZone.selectedCharacter = null;

        // Clear variation zone
        state.variationZone.variations = [];
        state.variationZone.activeVariation = null;
        state.variationZone.activeVersion = null;
        state.variationZone.samples = [];
        state.variationZone.editMode = 'view';

        state.sharedData.lastUpdated.books = Date.now();
        console.log('[BW_DATA] Successfully deleted book and cleared related data:', bookId);
      })
      .addCase(deleteBook.rejected, (state, action) => {
        state.sharedData.errors.books = action.payload;
        console.error('[BW_DATA] Failed to delete book:', action.payload);
      })

      // ===== CHARACTERS =====
      .addCase(fetchCharacters.pending, (state) => {
        state.characterZone.loading = true;
        state.characterZone.error = null;
      })
      .addCase(fetchCharacters.fulfilled, (state, action) => {
        state.characterZone.loading = false;
        state.characterZone.characters = action.payload;
        state.characterZone.lastUpdated = Date.now();
      })
      .addCase(fetchCharacters.rejected, (state, action) => {
        state.characterZone.loading = false;
        state.characterZone.error = action.payload;
      })

      // ===== ENHANCED CHARACTERS =====
      .addCase(fetchCharactersEnhanced.pending, (state) => {
        state.characterZone.loading = true;
        state.characterZone.error = null;
      })
      .addCase(fetchCharactersEnhanced.fulfilled, (state, action) => {
        state.characterZone.loading = false;
        state.characterZone.characters = action.payload.characters;
        state.characterZone.lastUpdated = Date.now();

        // Store raw API response for diagnostics
        state.characterZone.rawApiResponse = action.payload.rawApiResponse;

        console.log('[BW_DATA] Stored enhanced characters:', action.payload.characters.length, 'characters with opportunity data');
        console.log('[BW_DATA] Raw API response stored for diagnostics');
      })
      .addCase(fetchCharactersEnhanced.rejected, (state, action) => {
        state.characterZone.loading = false;
        state.characterZone.error = action.payload;
      })

      // ===== SINGLE CHARACTER =====
      .addCase(fetchCharacter.fulfilled, (state, action) => {
        const character = action.payload;
        const index = state.characterZone.characters.findIndex(c => c.uuid === character.uuid);

        if (index >= 0) {
          state.characterZone.characters[index] = character;
        } else {
          state.characterZone.characters.push(character);
        }

        // Update selected character if it matches
        if (state.sharedData.selections.characterId === character.uuid) {
          state.characterZone.selectedCharacter = character;
        }
      })

      // ===== CREATE CHARACTER =====
      .addCase(createCharacter.fulfilled, (state, action) => {
        const newCharacter = action.payload;
        state.characterZone.characters.push(newCharacter);
      })

      // ===== UPDATE CHARACTER =====
      .addCase(updateCharacter.fulfilled, (state, action) => {
        const { characterId, ...updatedData } = action.payload;
        const index = state.characterZone.characters.findIndex(c => c.uuid === characterId);

        if (index >= 0) {
          state.characterZone.characters[index] = { ...state.characterZone.characters[index], ...updatedData };
        }

        // Update selected character if it matches
        if (state.characterZone.selectedCharacter?.uuid === characterId) {
          state.characterZone.selectedCharacter = { ...state.characterZone.selectedCharacter, ...updatedData };
        }
      })

      // ===== VARIATIONS =====
      .addCase(fetchVariations.pending, (state) => {
        state.variationZone.loading = true;
        state.variationZone.error = null;
      })
      .addCase(fetchVariations.fulfilled, (state, action) => {
        state.variationZone.loading = false;
        state.variationZone.variations = action.payload.variations;
        state.variationZone.lastUpdated = Date.now();
        console.log('[BW_DATA] Stored variations for character:', action.payload.characterId, action.payload.variations.length, 'variations');
      })
      .addCase(fetchVariations.rejected, (state, action) => {
        state.variationZone.loading = false;
        state.variationZone.error = action.payload;
      })

      // ===== VARIATION COUNTS =====
      .addCase(fetchVariationCounts.pending, (state) => {
        console.log('[BW_DATA] fetchVariationCounts pending...');
      })
      .addCase(fetchVariationCounts.fulfilled, (state, action) => {
        const { counts } = action.payload;
        state.variationZone.variationCounts = counts;
        // console.log('[BW_DATA] ✅ Successfully stored variation counts for character:', characterId, counts);
        // console.log('[BW_DATA] ✅ Current variationCounts in state:', state.variationZone.variationCounts);
      })
      .addCase(fetchVariationCounts.rejected, (state, action) => {
        console.error('[BW_DATA] ❌ fetchVariationCounts failed:', action.payload);
        state.variationZone.error = action.payload;
      })

      // ===== LOAD VARIATION FOR EDIT =====
      .addCase(loadVariationForEdit.pending, (state) => {
        state.variationZone.loading = true;
        state.variationZone.error = null;
      })
      .addCase(loadVariationForEdit.fulfilled, (state, action) => {
        const { variation, samples } = action.payload;

        state.variationZone.loading = false;
        state.variationZone.activeVariation = variation;
        state.variationZone.samples = samples || [];
        state.variationZone.editMode = 'edit';
        state.variationZone.hasUnsavedChanges = false;
        state.variationZone.lastUpdated = Date.now();

        // In the loadVariationForEdit.fulfilled case
        state.variationZone.samples = (samples || []).sort((a, b) => {
          const versionA = parseInt(a.version) || 0;
          const versionB = parseInt(b.version) || 0;
          return versionA - versionB;
        });


        // Set active version from active sample or create a default one
        const activeSample = samples?.find(s => s.uuid === variation.activeSampleUuid);
        if (activeSample) {
          state.variationZone.activeVersion = {
            uuid: activeSample.uuid,
            textSnippet: activeSample.textSnippet,
            s3Url: activeSample.s3Url,
            fileDuration: activeSample.fileDuration,
            version: activeSample.version,
            isActive: true
          };
        } else {
          // Create a default active version if no samples exist
          state.variationZone.activeVersion = {
            uuid: null,
            textSnippet: variation.content || '',
            s3Url: '',
            fileDuration: 0,
            version: variation.version || 1,
            isActive: false
          };
        }

        console.log('[BW_DATA] Successfully loaded variation for edit:', {
          variation: variation.name,
          samples: samples?.length || 0,
          activeVersion: state.variationZone.activeVersion
        });
      })
      .addCase(loadVariationForEdit.rejected, (state, action) => {
        state.variationZone.loading = false;
        state.variationZone.error = action.payload;
      })

      // ===== CREATE VARIATION =====
      .addCase(createVariation.fulfilled, (state, action) => {
        const newVariation = action.payload;
        state.variationZone.variations.push(newVariation);
      })

      // ===== UPDATE VARIATION =====
      .addCase(updateVariation.fulfilled, (state, action) => {
        const { variationId, ...updatedData } = action.payload;
        const index = state.variationZone.variations.findIndex(v => v.uuid === variationId);

        if (index >= 0) {
          state.variationZone.variations[index] = { ...state.variationZone.variations[index], ...updatedData };
        }

        // Update active variation if it matches
        if (state.variationZone.activeVariation?.uuid === variationId) {
          state.variationZone.activeVariation = { ...state.variationZone.activeVariation, ...updatedData };
        }

        state.variationZone.hasUnsavedChanges = false;
      })

      // ===== DELETE VARIATION =====
      .addCase(deleteVariation.fulfilled, (state, action) => {
        const { variationId } = action.payload;

        // Remove from variations list
        state.variationZone.variations = state.variationZone.variations.filter(v => v.uuid !== variationId);

        // Clear active variation if it was deleted
        if (state.variationZone.activeVariation?.uuid === variationId) {
          state.variationZone.activeVariation = null;
          state.variationZone.activeVersion = null;
          state.variationZone.samples = [];
          state.variationZone.editMode = 'view';
        }
      })

      // ===== DELETE VERSION =====
      .addCase(deleteVersion.fulfilled, (state, action) => {
        const { versionId } = action.payload;

        // Remove from samples
        state.variationZone.samples = state.variationZone.samples.filter(s => s.uuid !== versionId);

        // Clear active version if it was deleted
        if (state.variationZone.activeVersion?.uuid === versionId) {
          state.variationZone.activeVersion = null;
        }
      })

      // ===== LOAD VERSION FOR EDIT =====
      .addCase(loadVersionForEdit.pending, (state) => {
        state.variationZone.loading = true;
        state.variationZone.error = null;
      })
      .addCase(loadVersionForEdit.fulfilled, (state, action) => {
        const { variation, samples, targetVersion } = action.payload;

        state.variationZone.loading = false;
        state.variationZone.activeVariation = variation;
        state.variationZone.samples = samples || [];
        state.variationZone.editMode = 'edit';
        state.variationZone.hasUnsavedChanges = false;
        state.variationZone.lastUpdated = Date.now();

        // Set the specific target version as active
        if (targetVersion) {
          state.variationZone.activeVersion = {
            uuid: targetVersion.uuid,
            textSnippet: targetVersion.textSnippet,
            s3Url: targetVersion.s3Url,
            fileDuration: targetVersion.fileDuration,
            version: targetVersion.version,
            isActive: true
          };
        }

        console.log('[BW_DATA] Successfully loaded specific version for edit:', targetVersion?.uuid);
      })
      .addCase(loadVersionForEdit.rejected, (state, action) => {
        state.variationZone.loading = false;
        state.variationZone.error = action.payload;
      })

      // ===== LOAD DEFAULT VERSION =====
      .addCase(loadDefaultVersion.pending, (state) => {
        state.variationZone.loading = true;
        state.variationZone.error = null;
      })
      .addCase(loadDefaultVersion.fulfilled, (state, action) => {
        const { variation, activeVersion } = action.payload;

        state.variationZone.loading = false;
        state.variationZone.activeVariation = variation;
        state.variationZone.activeVersion = activeVersion;
        state.variationZone.samples = [];
        state.variationZone.editMode = 'create';
        state.variationZone.hasUnsavedChanges = false;
        state.variationZone.lastUpdated = Date.now();

        console.log('[BW_DATA] Successfully loaded default version for new variation');
      })
      .addCase(loadDefaultVersion.rejected, (state, action) => {
        state.variationZone.loading = false;
        state.variationZone.error = action.payload;
      })

      // ===== LOAD VERSION FOR CLONE =====
      .addCase(loadVersionForClone.pending, (state) => {
        state.variationZone.loading = true;
        state.variationZone.error = null;
      })
      .addCase(loadVersionForClone.fulfilled, (state, action) => {
        const { variation, activeVersion } = action.payload;

        state.variationZone.loading = false;
        state.variationZone.activeVariation = variation;
        state.variationZone.activeVersion = activeVersion;
        state.variationZone.samples = [];
        state.variationZone.editMode = 'clone';
        state.variationZone.hasUnsavedChanges = false;
        state.variationZone.lastUpdated = Date.now();

        console.log('[BW_DATA] Successfully loaded version for cloning:', variation?.name);
      })
      .addCase(loadVersionForClone.rejected, (state, action) => {
        state.variationZone.loading = false;
        state.variationZone.error = action.payload;
      })

      // ===== SAVE VARIATION =====
      .addCase(saveVariation.pending, (state) => {
        state.variationZone.loading = true;
        state.variationZone.isSubmitting = true;
        state.variationZone.error = null;
      })
      .addCase(saveVariation.fulfilled, (state, action) => {
        const { variation, isCreate } = action.payload;

        // 🔍 DETAILED API RESPONSE LOGGING - Track exactly what the API returns
        console.log('[BW_DATA] saveVariation.fulfilled - DETAILED API RESPONSE ANALYSIS:');
        console.log('- Full action.payload:', action.payload);
        console.log('- variation object from API:', variation);
        console.log('- variation keys:', variation ? Object.keys(variation) : 'variation is null/undefined');
        console.log('- variation.activeSampleUuid:', variation?.activeSampleUuid);
        console.log('- variation.uuid:', variation?.uuid);
        console.log('- variation.name:', variation?.name);
        console.log('- isCreate:', isCreate);

        // Log current state before update
        console.log('- BEFORE UPDATE - Current activeVariation:', {
          uuid: state.variationZone.activeVariation?.uuid,
          activeSampleUuid: state.variationZone.activeVariation?.activeSampleUuid,
          name: state.variationZone.activeVariation?.name,
          allKeys: state.variationZone.activeVariation ? Object.keys(state.variationZone.activeVariation) : 'null'
        });

        state.variationZone.loading = false;
        state.variationZone.isSubmitting = false;
        state.variationZone.hasUnsavedChanges = false;
        state.variationZone.error = null;

        if (isCreate) {
          // Add new variation to list
          state.variationZone.variations.push(variation);

          // Update active variation with API response (wrapper fix ensures complete data)
          state.variationZone.activeVariation = { ...state.variationZone.activeVariation, ...variation };
          // Switch to edit mode after creation
          state.variationZone.editMode = 'edit';
        } else {
          // Update existing variation in list
          const index = state.variationZone.variations.findIndex(v => v.uuid === variation.uuid);
          if (index >= 0) {
            state.variationZone.variations[index] = { ...state.variationZone.variations[index], ...variation };
          }

          // Update active variation with API response (wrapper fix ensures complete data)
          state.variationZone.activeVariation = { ...state.variationZone.activeVariation, ...variation };
        }

        // Log final state after update
        console.log('- AFTER UPDATE - Final activeVariation:', {
          uuid: state.variationZone.activeVariation?.uuid,
          activeSampleUuid: state.variationZone.activeVariation?.activeSampleUuid,
          name: state.variationZone.activeVariation?.name
        });

        state.variationZone.lastUpdated = Date.now();
        console.log('[BW_DATA] Successfully saved variation:', variation?.name, 'isCreate:', isCreate);
      })
      .addCase(saveVariation.rejected, (state, action) => {
        state.variationZone.loading = false;
        state.variationZone.isSubmitting = false;
        state.variationZone.error = action.payload;
      })

      // ===== UPDATE ACTIVE SAMPLE =====
      .addCase(updateActiveSample.pending, (state) => {
        state.variationZone.loading = true;
        state.variationZone.error = null;
      })
      .addCase(updateActiveSample.fulfilled, (state, action) => {
        const { variationId, sampleId, variation } = action.payload;

        state.variationZone.loading = false;
        state.variationZone.error = null;

        // Update the variation with new active sample
        if (state.variationZone.activeVariation?.uuid === variationId) {
          state.variationZone.activeVariation.activeSampleUuid = sampleId;
        }

        // Update variation in list
        const index = state.variationZone.variations.findIndex(v => v.uuid === variationId);
        if (index >= 0) {
          state.variationZone.variations[index] = { ...state.variationZone.variations[index], ...variation };
        }

        // Update active version to match the new active sample
        const activeSample = state.variationZone.samples.find(s => s.uuid === sampleId);
        if (activeSample) {
          state.variationZone.activeVersion = {
            uuid: activeSample.uuid,
            textSnippet: activeSample.textSnippet,
            s3Url: activeSample.s3Url,
            fileDuration: activeSample.fileDuration,
            version: activeSample.version,
            isActive: true
          };
        }

        state.variationZone.lastUpdated = Date.now();
        console.log('[BW_DATA] Successfully updated active sample:', sampleId);
      })
      .addCase(updateActiveSample.rejected, (state, action) => {
        state.variationZone.loading = false;
        state.variationZone.error = action.payload;
      })

      // ===== UPDATE TEXT SNIPPET =====
      .addCase(updateTextSnippetDb.pending, (state) => {
        state.variationZone.loading = true;
        state.variationZone.error = null;
      })
      .addCase(updateTextSnippetDb.fulfilled, (state, action) => {
        const { sampleuuid, s3Url, content } = action.payload;

        state.variationZone.loading = false;
        state.variationZone.error = null;

        // Update active version if it matches
        if (state.variationZone.activeVersion?.uuid === sampleuuid) {
          if (s3Url) state.variationZone.activeVersion.s3Url = s3Url;
          if (content !== undefined) state.variationZone.activeVersion.textSnippet = content;
        }

        // Update sample in samples array
        const sampleIndex = state.variationZone.samples.findIndex(s => s.uuid === sampleuuid);
        if (sampleIndex >= 0) {
          if (s3Url) state.variationZone.samples[sampleIndex].s3Url = s3Url;
          if (content !== undefined) state.variationZone.samples[sampleIndex].textSnippet = content;
        }

        state.variationZone.lastUpdated = Date.now();
        console.log('[BW_DATA] Successfully updated text snippet in state');
      })
      .addCase(updateTextSnippetDb.rejected, (state, action) => {
        state.variationZone.loading = false;
        state.variationZone.error = action.payload;
      });
  }
});

// Export actions
export const {
  setLitGroupSelection,
  autoSelectFirstBook,
  autoSelectFirstCharacter,
  setBookSelection,
  setCharacterSelection,
  setVariationSelection,
  updateVersionField,
  setActiveVersionFromSamples,
  setEditMode,
  clearUnsavedChanges,
  setSubmitting,
  clearCharacterZone,
  clearVariationZone,
  updateFormField,
  setFormDirty,
  setFormSubmitting,
  setEditError,
  clearEditError,
  clearEditForm,
  setRepositoryVersion,
  showErrorHint
} = bookWorksheetDataSlice.actions;

// ===== CHANGE COUNTER SELECTORS FOR STABLE HOOKS =====
// These selectors provide change counting for stable hooks compatibility

export const selectCharacterZoneChangeCount = (state) => {
  const characterZone = state.user.bookWorksheetData?.characterZone;
  return characterZone?.lastUpdated || 0;
};

export const selectVariationFormChangeCount = (state) => {
  const variationZone = state.user.bookWorksheetData?.variationZone;
  // Combine multiple change indicators for form stability
  return (variationZone?.lastUpdated || 0) +
    (variationZone?.hasUnsavedChanges ? 1 : 0) +
    (variationZone?.editMode === 'edit' ? 1 : 0);
};

export const selectVariationListChangeCount = (state) => {
  const variationZone = state.user.bookWorksheetData?.variationZone;
  return variationZone?.lastUpdated || 0;
};

export const selectVariationVersionsListChangeCount = (state) => {
  const variationZone = state.user.bookWorksheetData?.variationZone;
  // Use samples array length and last updated as change indicators
  return (variationZone?.lastUpdated || 0) + (variationZone?.samples?.length || 0);
};

// Export reducer
export default bookWorksheetDataSlice.reducer;
